/**
 * Prices travel as integer cents. These convert to and from what a person
 * types in a price box. US dollars only — every store is in the US.
 */

/** "$5.99" — for display. Empty string for no price. */
export function formatCents(cents: number | null | undefined): string {
  if (cents == null) return '';
  return `$${(cents / 100).toFixed(2)}`;
}

/** "5.99" — the starting text of a price input. */
export function centsToInput(cents: number | null | undefined): string {
  return cents == null ? '' : (cents / 100).toFixed(2);
}

export type ParsedMoney = { ok: true; cents: number | null } | { ok: false };

const MAX_CENTS = 10_000_000;

/**
 * What the person typed, as cents. Blank is a valid answer (no price). Accepts
 * "5", "5.9", "5.99", "$5.99", "1,299.00"; rejects anything else, a zero, and
 * more than two decimals — a mistyped price must not be silently rounded.
 */
export function parseMoney(input: string): ParsedMoney {
  const typed = input.trim().replace(/^\$/, '').trim();
  if (typed === '') return { ok: true, cents: null };
  // A comma is only ever a thousands separator ("1,299.00"). "5,99" is someone
  // typing a decimal comma, and reading it as $599 would be a hundredfold error.
  if (typed.includes(',') && !/^\d{1,3}(,\d{3})+(\.\d{1,2})?$/.test(typed)) return { ok: false };
  const text = typed.replace(/,/g, '');
  if (!/^\d{1,6}(\.\d{1,2})?$/.test(text)) return { ok: false };
  const [whole, frac = ''] = text.split('.');
  const cents = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
  if (cents < 1 || cents > MAX_CENTS) return { ok: false };
  return { ok: true, cents };
}

/** "Sep 20, 2026" from "2026-09-20", in the reader's language. Never shifts the day. */
export function formatDay(day: string | null | undefined, language: string): string {
  if (!day) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return '';
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toLocaleDateString(language.startsWith('es') ? 'es-US' : 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Today as `YYYY-MM-DD` in the browser's zone — the earliest day a sale may end. */
export function todayDay(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
